<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import responseProductDTO = Modules.CampaignPlanner.Data.PriceQuoteApi.responseProductDTO;

const props = defineProps<{
	clusterId?: number;
	map?: any;
	sourceId?: string;
	coordinates: [number, number];
	allProducts: responseProductDTO[];
	singleReferenceId?: string;
	getReach: (product: responseProductDTO) => number;
	getPopup: () => any;
	onToggle: (product: responseProductDTO) => void;
}>();

const resolvedProducts = ref<responseProductDTO[]>([]);
const loading = ref(false);
const localSelected = ref<Record<string, boolean>>({});

const getKey = (p: responseProductDTO) => p.reference_id ?? p.name ?? "";

const formatReach = (n: number | null | undefined): string => {
	if (!n) return "—";
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
	return String(Math.round(n));
};

const initLocal = () => {
	const m: Record<string, boolean> = {};
	for (const p of resolvedProducts.value) {
		m[getKey(p)] = p.isSelected ?? false;
	}
	localSelected.value = m;
};

onMounted(() => {
	if (props.clusterId !== undefined && props.map && props.sourceId) {
		loading.value = true;
		props.map
			.getSource(props.sourceId)
			.getClusterLeaves(
				props.clusterId,
				Infinity,
				0,
				(err: any, features: any[]) => {
					loading.value = false;
					if (!err) {
						const ids = features
							.map((f) => f.properties?.reference_id as string)
							.filter(Boolean);
						resolvedProducts.value = ids
							.map((id) => props.allProducts.find((p) => p.reference_id === id))
							.filter((p): p is responseProductDTO => !!p);
					}
					initLocal();
				}
			);
	} else {
		const p = props.singleReferenceId
			? props.allProducts.find((x) => x.reference_id === props.singleReferenceId)
			: null;
		resolvedProducts.value = p ? [p] : [];
		initLocal();
	}
});

const allSelected = computed(() =>
	resolvedProducts.value.length > 0 &&
	resolvedProducts.value.every((p) => localSelected.value[getKey(p)])
);

const toggle = (p: responseProductDTO) => {
	const key = getKey(p);
	localSelected.value[key] = !localSelected.value[key];
	props.onToggle(p);
};

const toggleAll = () => {
	const target = !allSelected.value;
	for (const p of resolvedProducts.value) {
		if ((localSelected.value[getKey(p)] ?? false) !== target) {
			toggle(p);
		}
	}
};

const zoomIn = () => {
	if (props.clusterId === undefined || !props.map || !props.sourceId) return;
	props.map
		.getSource(props.sourceId)
		.getClusterExpansionZoom(props.clusterId, (err: any, zoom: number) => {
			if (err) return;
			props.map.easeTo({ center: props.coordinates, zoom });
		});
	props.getPopup()?.remove();
};
</script>

<template>
	<div class="frm-popup">
		<!-- Header -->
		<div class="frm-popup__header">
			<span class="frm-popup__title">
				{{ resolvedProducts.length }} Screen{{ resolvedProducts.length !== 1 ? "s" : "" }}
			</span>
			<button
				v-if="resolvedProducts.length > 1"
				class="frm-popup__select-all-btn"
				@click="toggleAll"
			>
				{{ allSelected ? "Deselect all" : "Select all" }}
			</button>
		</div>

		<div class="frm-popup__divider" />

		<!-- Loading -->
		<div v-if="loading" class="frm-popup__loading">Loading…</div>

		<!-- Product list -->
		<div v-else-if="resolvedProducts.length > 0" class="frm-popup__list">
			<div
				v-for="p in resolvedProducts"
				:key="getKey(p)"
				class="frm-popup__row"
				@click="toggle(p)"
			>
				<div class="frm-popup__checkbox" :class="{ 'frm-popup__checkbox--checked': localSelected[getKey(p)] }">
					<svg v-if="localSelected[getKey(p)]" width="10" height="10" viewBox="0 0 10 10">
						<polyline points="1.5,5 4,7.5 8.5,2.5" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</div>
				<span class="frm-popup__name" :title="p.name ?? ''">{{ p.name ?? "—" }}</span>
				<span class="frm-popup__reach">{{ formatReach(getReach(p)) }}</span>
			</div>
		</div>

		<div v-else class="frm-popup__loading">No screens found</div>

		<!-- Cluster zoom -->
		<template v-if="clusterId !== undefined && !loading">
			<div class="frm-popup__divider" />
			<button class="frm-popup__zoom-btn" @click="zoomIn">Zoom in</button>
		</template>
	</div>
</template>

<style scoped>
.frm-popup {
	width: 280px;
	color: #fff;
}

.frm-popup__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
}

.frm-popup__title {
	font-size: 0.75rem;
	opacity: 0.65;
}

.frm-popup__select-all-btn {
	background: rgba(255, 255, 255, 0.12);
	border: 1px solid rgba(255, 255, 255, 0.2);
	color: #fff;
	border-radius: 4px;
	padding: 2px 8px;
	font-size: 0.7rem;
	cursor: pointer;
	transition: background 0.15s;
	white-space: nowrap;
}
.frm-popup__select-all-btn:hover {
	background: rgba(255, 255, 255, 0.22);
}

.frm-popup__divider {
	height: 1px;
	background: rgba(255, 255, 255, 0.15);
	margin: 8px 0;
}

.frm-popup__loading {
	font-size: 0.75rem;
	opacity: 0.55;
	padding: 4px 0;
}

.frm-popup__list {
	display: flex;
	flex-direction: column;
	gap: 2px;
	max-height: 240px;
	overflow-y: auto;
}

.frm-popup__row {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 6px;
	border-radius: 5px;
	cursor: pointer;
	transition: background 0.12s;
}
.frm-popup__row:hover {
	background: rgba(255, 255, 255, 0.08);
}

.frm-popup__checkbox {
	flex-shrink: 0;
	width: 14px;
	height: 14px;
	border-radius: 3px;
	border: 1.5px solid rgba(255, 255, 255, 0.4);
	background: transparent;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.12s, border-color 0.12s;
}
.frm-popup__checkbox--checked {
	background: #2563eb;
	border-color: #2563eb;
}

.frm-popup__name {
	flex: 1;
	font-size: 0.8rem;
	font-weight: 500;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	line-height: 1.3;
}

.frm-popup__reach {
	flex-shrink: 0;
	font-size: 0.75rem;
	opacity: 0.65;
	font-variant-numeric: tabular-nums;
}

.frm-popup__zoom-btn {
	width: 100%;
	background: rgba(255, 255, 255, 0.12);
	border: 1px solid rgba(255, 255, 255, 0.2);
	color: #fff;
	border-radius: 6px;
	padding: 5px 0;
	font-size: 0.8rem;
	cursor: pointer;
	transition: background 0.15s;
}
.frm-popup__zoom-btn:hover {
	background: rgba(255, 255, 255, 0.2);
}
</style>
